import { useState, useEffect, useMemo } from 'react'
import s from './List.scss'
import { WeekPicker, InputContainer, SwitchToggle } from 'components/ideal-core-ui'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { useLoading } from 'hooks'
import { getUser } from 'utils/authService'
import { makeReportsPostRequest, makeGetRequest, makePostRequest } from 'utils/api'
import { utcDate } from 'utils/helpers'
import { toResult, toOptions, fromResult } from './serializers'
import moment from 'moment'
import Select from 'react-select'
import { FaPlusCircle, FaTrashAlt, FaEdit, FaStickyNote, FaInfoCircle } from 'react-icons/fa'
import { Button, UncontrolledTooltip } from 'reactstrap'
import { WORK_TYPES } from './constants'
import { v4 as uuidv4 } from 'uuid'
import { useImmer } from 'use-immer'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { NotesModal } from './NotesModal'
import { useDispatch } from 'react-redux'

const loggedInUser = getUser()

const initialResult = {
  id: 0,
  deal: null,
  clients: [],
  client: null,
  service: null,
  workType: WORK_TYPES[0],
  hours: [0, 0, 0, 0, 0, 0, 0],
  readOnly: false,
  notes: ['', '', '', '', '', '', ''],
  billable: true
}

const TimeTrackerList = ({ settings, selectedWeek, setSelectedWeek, handleWeekChange }) => {
  const [, withLoadingLocks] = useLoading()
  const [result, setResult] = useImmer([initialResult])
  const [typesOfWork, setTypesOfWork] = useState([])
  const [typesOfTimeoff, setTypesOfTimeoff] = useState([])
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const dispatch = useDispatch()

  const toggleNotesModal = () => {
    setSelectedRow(null)
    setShowNotesModal(!showNotesModal)
  }

  const totalHours = useMemo(() => {
    return result.reduce((acc, row) => {
      return acc + row.hours.reduce((acc, h) => acc + Number(h), 0)
    }, 0)
  }, [result])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/timetracker/type-of-work')
      setTypesOfWork(toOptions(response, { isTimeOff: false }))

      const types = await makeGetRequest('/v1/timetracker/time-off-type')
      setTypesOfTimeoff(
        toOptions(
          types.filter(t => !t.system),
          { isTimeOff: true }
        )
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!selectedWeek.length) return
    ;(async () => {
      const { weekReport } = await withLoadingLocks(
        makeReportsPostRequest('/v1/reports/time-tracker/work-week', {
          from: utcDate(selectedWeek[0]),
          userId: loggedInUser.id
        })
      )

      if (!weekReport.length) {
        setResult([initialResult])
        return
      }

      setResult(toResult(weekReport))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek])

  const addRow = () => {
    setResult([...result, { ...initialResult, id: uuidv4() }])
  }

  const deleteRow = id => {
    const index = result.findIndex(r => r.id === id)
    setResult(result.filter((_, i) => i !== index))
  }

  const getDealCompanies = async (dealId, row) => {
    const index = result.findIndex(r => r.id === row.id)
    const response = await makeGetRequest(`/v1/deals/${dealId}/companies`)

    setResult(draft => {
      draft[index].clients = response.map(({ company }) => toCompaniesSelect([company])[0])
    })

    if (response.length === 1) {
      setResult(draft => {
        draft[index].client = toCompaniesSelect([response[0].company])[0]
      })
    } else {
      setResult(draft => {
        draft[index].client = null
      })
    }
  }

  const handleSelectDeal = (value, row) => {
    handleChangeProperty(value, row, 'deal')
    getDealCompanies(value.value, row)
  }

  const handleChangeProperty = (value, row, property) => {
    const index = result.findIndex(r => r.id === row.id)

    if (Array.isArray(property)) {
      setResult(draft => {
        draft[index][property[0]] = value[0]
        draft[index][property[1]] = value[1]
      })
      return
    }

    setResult(draft => {
      draft[index][property] = value
    })
  }

  const handleHoursChange = (value, row, index) => {
    const i = result.findIndex(r => r.id === row.id)
    setResult(draft => {
      draft[i].hours[index] = value
    })
  }

  const handleNotesChange = (values, row) => {
    const i = result.findIndex(r => r.id === row.id)

    setResult(draft => {
      draft[i].notes = values
    })
  }

  const onSubmit = async () => {
    try {
      await makePostRequest(
        '/v1/timetracker/bulk-create',
        fromResult(
          result.filter(r => !r.readOnly),
          selectedWeek,
          loggedInUser.id
        )
      )
      setResult(draft => {
        draft.forEach(r => {
          r.readOnly = true
        })
      })
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Saved successfuly',
          level: 'success'
        }
      })
    } catch (error) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem saving list',
          level: 'error'
        }
      })
    }
  }

  const isDisabled = useMemo(() => {
    return (
      totalHours === 0 ||
      result.every(value => value.readOnly) ||
      result.some(value => !value.deal && !value.service?.isTimeOff) ||
      result.some(value => !value.service)
    )
  }, [result, totalHours])

  return (
    <>
      <div className={s.container}>
        <header style={{ display: 'flex', marginBottom: 50 }}>
          <WeekPicker
            onChange={handleWeekChange}
            weekStartsOn={settings.weekStart === 'MONDAY' ? 1 : 0}
            value={selectedWeek}
            cb={setSelectedWeek}
          />
          <span style={{ marginLeft: 50 }}>
            Total <b>{totalHours}</b> hour(s)
          </span>
          <Button className={s.saveBtn} disabled={isDisabled} onClick={onSubmit}>
            Save
          </Button>
        </header>
        {result.map(row => {
          return (
            <div key={row?.id} className={s.row}>
              <div className={s.column}>
                <label className={s.label}>*Opportunity</label>
                <PaginatedSelect
                  className={s.dropdown}
                  url="/v1/timetracker/simple-deals"
                  onChange={value => {
                    handleSelectDeal(value, row)
                  }}
                  serializer={toOptions}
                  value={row.deal}
                  isDisabled={row.readOnly}
                />
              </div>
              <div className={s.column}>
                <label className={s.label}>*Service</label>
                <Select
                  onChange={value => {
                    handleChangeProperty([value, !value.isTimeOff], row, ['service', 'billable'])
                  }}
                  options={[...typesOfWork, ...typesOfTimeoff]}
                  className={s.dropdown}
                  value={row.service}
                  isDisabled={row.readOnly}
                />
              </div>
              <div className={s.column}>
                <label className={s.label}>Company</label>
                <Select
                  onChange={value => {
                    handleChangeProperty(value, row, 'client')
                  }}
                  options={row.clients}
                  className={s.dropdown}
                  value={row.client}
                  isDisabled={!row.deal || row.readOnly}
                  isClearable
                />
              </div>
              {selectedWeek.map((day, i) => {
                return (
                  <div
                    key={i}
                    style={{ width: 70, position: 'relative', bottom: 5, marginRight: 10 }}
                  >
                    <label className={s.label}>
                      <strong>{moment(day).format('DD ')}</strong>
                      {moment(day).format('ddd')}
                    </label>
                    <InputContainer
                      type="number"
                      classname={s.input}
                      cb={value => {
                        handleHoursChange(value, row, i)
                      }}
                      text={row.hours[i]}
                      min={0}
                      isDisabled={row.readOnly}
                    />
                    {row.notes[i] && (
                      <>
                        <FaStickyNote
                          title="Has note"
                          className={s.notesIcon}
                          onClick={() => {
                            toggleNotesModal()
                            setSelectedRow(row)
                          }}
                          id={'info1' + row.id}
                        />
                        <UncontrolledTooltip placement="top" target={'info1' + row.id}>
                          View Description of Work Activity
                        </UncontrolledTooltip>
                      </>
                    )}
                  </div>
                )
              })}
              <div className={s.column}>
                <label className={s.label}>Work Type</label>
                <Select
                  onChange={value => {
                    handleChangeProperty(value, row, 'workType')
                  }}
                  options={WORK_TYPES}
                  className={s.dropdown}
                  value={row.workType}
                  isDisabled={row.readOnly}
                />
              </div>
              <div style={{ width: 65, position: 'relative', top: 10 }}>
                <label className={s.label}>Billable</label>
                <SwitchToggle
                  defaultOn={row.billable}
                  noText
                  id={`billable-${row.id}`}
                  onToggle={value => {
                    handleChangeProperty(value, row, 'billable')
                  }}
                  isDisabled={row.readOnly || row.service?.isTimeOff}
                />
              </div>
              <div
                className={s.deleteColumn}
                style={{ visibility: row.hours.some(h => h > 0) ? 'visible' : 'hidden' }}
              >
                {row.readOnly ? (
                  <>
                    <FaInfoCircle
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        toggleNotesModal()
                        setSelectedRow(row)
                      }}
                      id={'info' + row.id}
                    />
                    <UncontrolledTooltip placement="top" target={'info' + row.id}>
                      View Description of Work Activity
                    </UncontrolledTooltip>
                  </>
                ) : (
                  <FaEdit
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      toggleNotesModal()
                      setSelectedRow(row)
                    }}
                  />
                )}
              </div>
              <div className={s.deleteColumn}>
                {result.length > 1 && !row.readOnly && (
                  <FaTrashAlt style={{ cursor: 'pointer' }} onClick={() => deleteRow(row.id)} />
                )}
              </div>
            </div>
          )
        })}
        <span style={{ cursor: 'pointer', position: 'relative', top: 10 }} onClick={addRow}>
          <FaPlusCircle /> Add row
        </span>
      </div>
      {showNotesModal && (
        <NotesModal
          showNotesModal={showNotesModal}
          toggleNotesModal={toggleNotesModal}
          row={selectedRow}
          selectedWeek={selectedWeek}
          handleNotesChange={handleNotesChange}
        />
      )}
    </>
  )
}

export default TimeTrackerList
